import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { categoryService, monthlyPlanService } from '../../../../apiServices';
import {
  Category,
  Currency,
  MonthlyPlan,
  MonthlyPlanActivity,
  MonthlyPlanTemplate,
  MonthlyPlanTemplateFixedExpense,
  UpdateMonthlyPlanRequest
} from '../../../../apiServices/types';
import { useNotification } from '../../../../contexts/NotificationContext';
import {
  computeMonthlyPlan,
  formatCurrency,
  formatPeriod,
  getCurrencyName,
  parseCurrency,
  PlanInput,
  PlanPeriod,
  roundMoney,
  sortByAmountDesc,
  toLocalDateString,
  ValidationConstants,
  ValidationUtils
} from '../../../../utils';
import { ConfirmationModal, GlassSelect } from '../../../UI';
import WeeklyBreakdown from './WeeklyBreakdown';
import { getRecurrenceLabel, parseAmountInput, toAmountInput } from './planDisplay';

interface MonthlyPlanEditorProps {
  period: PlanPeriod;
  template: MonthlyPlanTemplate;
  categories: Category[];
  plan: MonthlyPlan | null;
  activity: MonthlyPlanActivity | null;
  onCancel: () => void;
  onSaved: (plan: MonthlyPlan) => void;
}

interface IncomeRow {
  key: string;
  name: string;
  amount: string;
}

interface FixedRow {
  key: string;
  name: string;
  amount: string;
  paymentsCount: number;
  dayOfMonth: number | null;
  recurringExpenseId: string | null;
  recurrenceType: string | null;
  categoryId: string;
  categoryName: string | null;
  accountName: string | null;
  isSelected: boolean;
  isCustom: boolean;
}

interface SavingsRow {
  key: string;
  accountId: string;
  accountName: string;
  currency: Currency | string;
  currentBalance: number | null;
  amount: string;
  isSelected: boolean;
}

interface EditorRows {
  incomes: IncomeRow[];
  fixedExpenses: FixedRow[];
  savingsGoals: SavingsRow[];
}

const createIncomeRow = (name = '', amount = 0): IncomeRow => ({
  key: uuidv4(),
  name,
  amount: toAmountInput(amount)
});

const createCustomRow = (overrides: Partial<FixedRow> = {}): FixedRow => ({
  key: uuidv4(),
  name: '',
  amount: '',
  paymentsCount: 1,
  dayOfMonth: null,
  recurringExpenseId: null,
  recurrenceType: null,
  categoryId: '',
  categoryName: null,
  accountName: null,
  isSelected: true,
  isCustom: true,
  ...overrides
});

const createTemplateRow = (item: MonthlyPlanTemplateFixedExpense): FixedRow => ({
  key: uuidv4(),
  name: item.name,
  amount: toAmountInput(item.amount),
  paymentsCount: item.paymentsCount,
  dayOfMonth: item.dayOfMonth ?? null,
  recurringExpenseId: item.recurringExpenseId ?? null,
  recurrenceType: item.recurrenceType ?? null,
  categoryId: '',
  categoryName: item.categoryName ?? null,
  accountName: item.accountName ?? null,
  isSelected: item.isSelected,
  isCustom: false
});

const buildUnsortedEditorRows = (template: MonthlyPlanTemplate, plan: MonthlyPlan | null): EditorRows => {
  const catalogTemplates = template.fixedExpenses.filter(item => item.recurringExpenseId);

  if (!plan) {
    return {
      incomes: template.incomes.map(income => createIncomeRow(income.name, income.amount)),
      fixedExpenses: template.fixedExpenses.map(item => item.recurringExpenseId
        ? createTemplateRow(item)
        : createCustomRow({
          name: item.name,
          amount: toAmountInput(item.amount),
          paymentsCount: item.paymentsCount,
          dayOfMonth: item.dayOfMonth ?? null,
          categoryId: item.categoryId ?? '',
          categoryName: item.categoryName ?? null
        })),
      savingsGoals: template.savingsGoals.map(goal => ({
        key: uuidv4(),
        accountId: goal.accountId,
        accountName: goal.accountName,
        currency: goal.currency,
        currentBalance: goal.currentBalance,
        amount: toAmountInput(goal.amount),
        isSelected: goal.isSelected
      }))
    };
  }

  const planTemplateItems = new Map(plan.fixedExpenses
    .filter(item => item.recurringExpenseId)
    .map(item => [item.recurringExpenseId as string, item]));
  const planGoals = new Map(plan.savingsGoals.map(goal => [goal.accountId, goal]));

  return {
    incomes: plan.incomes.map(income => createIncomeRow(income.name, income.amount)),
    fixedExpenses: [
      ...catalogTemplates.map(item => {
        const planItem = planTemplateItems.get(item.recurringExpenseId as string);
        return {
          ...createTemplateRow(item),
          amount: toAmountInput(planItem ? planItem.amount : item.amount),
          paymentsCount: planItem ? planItem.paymentsCount : item.paymentsCount,
          isSelected: !!planItem
        };
      }),
      ...plan.fixedExpenses
        .filter(item => item.recurringExpenseId && !catalogTemplates.some(t => t.recurringExpenseId === item.recurringExpenseId))
        .map(item => createTemplateRow({ ...item, isSelected: true })),
      ...plan.fixedExpenses
        .filter(item => !item.recurringExpenseId)
        .map(item => createCustomRow({
          name: item.name,
          amount: toAmountInput(item.amount),
          paymentsCount: item.paymentsCount,
          dayOfMonth: item.dayOfMonth ?? null,
          categoryId: item.categoryId ?? '',
          categoryName: item.categoryName ?? null
        }))
    ],
    savingsGoals: [
      ...template.savingsGoals.map(goal => {
        const planGoal = planGoals.get(goal.accountId);
        return {
          key: uuidv4(),
          accountId: goal.accountId,
          accountName: goal.accountName,
          currency: goal.currency,
          currentBalance: goal.currentBalance,
          amount: toAmountInput(planGoal ? planGoal.amount : goal.amount),
          isSelected: !!planGoal
        };
      }),
      ...plan.savingsGoals
        .filter(goal => !template.savingsGoals.some(t => t.accountId === goal.accountId))
        .map(goal => ({
          key: uuidv4(),
          accountId: goal.accountId,
          accountName: goal.accountName,
          currency: goal.currency,
          currentBalance: null,
          amount: toAmountInput(goal.amount),
          isSelected: true
        }))
    ]
  };
};

// The template is the catalog of the month; an existing plan only overrides selection and amounts.
// Rows are sorted by amount once, so they don't jump while editing and new rows stay at the bottom
const buildEditorRows = (template: MonthlyPlanTemplate, plan: MonthlyPlan | null): EditorRows => {
  const rows = buildUnsortedEditorRows(template, plan);

  return {
    ...rows,
    fixedExpenses: sortByAmountDesc(rows.fixedExpenses, row => parseAmountInput(row.amount))
  };
};

const PlusIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ marginRight: '6px' }}>
    <path d="M12 2v20M2 12h20"></path>
  </svg>
);

const TrashIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3,6 5,6 21,6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);

const InfoIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="12"></line>
    <line x1="12" y1="16" x2="12.01" y2="16"></line>
  </svg>
);

const MonthlyPlanEditor: React.FC<MonthlyPlanEditorProps> = ({
  period,
  template,
  categories: initialCategories,
  plan,
  activity,
  onCancel,
  onSaved
}) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [initialRows] = useState<EditorRows>(() => buildEditorRows(template, plan));
  const [incomes, setIncomes] = useState<IncomeRow[]>(initialRows.incomes);
  const [fixedExpenses, setFixedExpenses] = useState<FixedRow[]>(initialRows.fixedExpenses);
  const [savingsGoals, setSavingsGoals] = useState<SavingsRow[]>(initialRows.savingsGoals);
  const [categories, setCategories] = useState<Category[]>(initialCategories);
  const [errors, setErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [todayKey] = useState(() => toLocalDateString(new Date()));

  const templateRows = fixedExpenses.filter(row => !row.isCustom);
  const selectedTemplateRows = templateRows.filter(row => row.isSelected);

  const categoryOptions = useMemo(
    () => categories.map(category => ({ value: category.id, label: category.name })),
    [categories]
  );

  const draft = useMemo<PlanInput>(() => ({
    year: period.year,
    month: period.month,
    incomes: incomes.map(row => ({ amount: parseAmountInput(row.amount) })),
    fixedExpenses: fixedExpenses
      .filter(row => row.isSelected)
      .map(row => ({
        key: row.key,
        amount: parseAmountInput(row.amount),
        paymentsCount: row.paymentsCount,
        dayOfMonth: row.dayOfMonth,
        recurringExpenseId: row.isCustom ? null : row.recurringExpenseId,
        categoryId: row.isCustom ? row.categoryId || null : null
      })),
    savingsGoals: savingsGoals
      .filter(row => row.isSelected)
      .map(row => ({ key: row.key, accountId: row.accountId, amount: parseAmountInput(row.amount) }))
  }), [period, incomes, fixedExpenses, savingsGoals]);

  const summary = useMemo(() => computeMonthlyPlan(draft, activity, todayKey), [draft, activity, todayKey]);
  const hasMonthActivity = summary.variableSpent + summary.fixedActual + summary.savingsActual + summary.unplannedSavings > 0;

  const hasChanges = (): boolean =>
    JSON.stringify({ incomes, fixedExpenses, savingsGoals }) !== JSON.stringify(initialRows);

  const updateIncome = (key: string, patch: Partial<IncomeRow>): void =>
    setIncomes(rows => rows.map(row => (row.key === key ? { ...row, ...patch } : row)));

  const updateFixed = (key: string, patch: Partial<FixedRow>): void =>
    setFixedExpenses(rows => rows.map(row => (row.key === key ? { ...row, ...patch } : row)));

  const updateSavings = (key: string, patch: Partial<SavingsRow>): void =>
    setSavingsGoals(rows => rows.map(row => (row.key === key ? { ...row, ...patch } : row)));

  const setAllTemplatesSelected = (isSelected: boolean): void =>
    setFixedExpenses(rows => rows.map(row => (row.isCustom ? row : { ...row, isSelected })));

  const handleCreateCategory = async (rowKey: string, categoryName: string): Promise<void> => {
    const name = categoryName.trim();

    if (name.length < ValidationConstants.Category.NameMinLength) {
      showNotification(`Назва категорії має бути не менше ${ValidationConstants.Category.NameMinLength} символів`, 'error');
      return;
    }
    if (name.length > ValidationConstants.Category.NameMaxLength) {
      showNotification(`Назва категорії не може перевищувати ${ValidationConstants.Category.NameMaxLength} символів`, 'error');
      return;
    }
    if (ValidationUtils.containsForbiddenChars(name)) {
      showNotification('Назва категорії містить заборонені символи', 'error');
      return;
    }

    const existingCategory = categories.find(category => category.name.toLowerCase() === name.toLowerCase());
    if (existingCategory) {
      updateFixed(rowKey, { categoryId: existingCategory.id, categoryName: existingCategory.name });
      return;
    }

    try {
      const newCategory = await categoryService.create({ name });
      setCategories(prev => [...prev, newCategory]);
      updateFixed(rowKey, { categoryId: newCategory.id, categoryName: newCategory.name });
      showNotification('Категорію успішно створено', 'success');
    } catch (error: any) {
      showNotification(error.message || 'Помилка створення категорії', 'error');
    }
  };

  const validate = (): string[] => {
    const messages: string[] = [];
    const filledIncomes = incomes.filter(row => row.name.trim() || row.amount.trim());

    if (!filledIncomes.some(row => parseAmountInput(row.amount) > 0)) {
      messages.push('Вкажіть дохід на місяць');
    }

    filledIncomes.forEach(row => {
      const name = row.name.trim();
      if (!name) {
        messages.push('Вкажіть назву для кожного доходу');
      } else if (ValidationUtils.containsForbiddenChars(name)) {
        messages.push(`Назва «${name}» містить заборонені символи (<, >, ", &, ')`);
      } else if (parseAmountInput(row.amount) <= 0) {
        messages.push(`Вкажіть суму доходу «${name}»`);
      }
    });

    const usedCategories = new Set<string>();
    fixedExpenses.filter(row => row.isSelected).forEach(row => {
      const name = row.name.trim();

      if (row.isCustom) {
        if (!name) {
          messages.push('Вкажіть назву для кожної власної витрати');
          return;
        }
        if (ValidationUtils.containsForbiddenChars(name)) {
          messages.push(`Назва «${name}» містить заборонені символи (<, >, ", &, ')`);
        }
        if (!row.categoryId) {
          messages.push(`Оберіть категорію для «${name}» — за нею витрати зараховуватимуться автоматично`);
        } else if (usedCategories.has(row.categoryId)) {
          messages.push(`Категорія «${row.categoryName ?? ''}» вже використана в іншій власній витраті`);
        } else {
          usedCategories.add(row.categoryId);
        }
      }

      if (parseAmountInput(row.amount) <= 0) {
        messages.push(`Вкажіть суму для «${name}»`);
      }
    });

    savingsGoals.filter(row => row.isSelected).forEach(row => {
      if (parseAmountInput(row.amount) <= 0) {
        messages.push(`Вкажіть суму заощаджень для «${row.accountName}»`);
      }
    });

    return Array.from(new Set(messages));
  };

  const buildRequest = (): UpdateMonthlyPlanRequest => ({
    incomes: incomes
      .filter(row => row.name.trim() && parseAmountInput(row.amount) > 0)
      .map(row => ({ name: row.name.trim(), amount: roundMoney(parseAmountInput(row.amount)) })),
    fixedExpenses: fixedExpenses
      .filter(row => row.isSelected)
      .map(row => ({
        name: row.name.trim(),
        amount: roundMoney(parseAmountInput(row.amount)),
        paymentsCount: row.paymentsCount,
        dayOfMonth: row.dayOfMonth,
        recurringExpenseId: row.isCustom ? null : row.recurringExpenseId,
        categoryId: row.isCustom ? row.categoryId : null
      })),
    savingsGoals: savingsGoals
      .filter(row => row.isSelected)
      .map(row => ({ accountId: row.accountId, amount: roundMoney(parseAmountInput(row.amount)) }))
  });

  const handleSave = async (): Promise<void> => {
    const validationErrors = validate();
    setErrors(validationErrors);

    if (validationErrors.length > 0) {
      showNotification(validationErrors[0], 'error');
      return;
    }

    setIsSaving(true);
    try {
      const request = buildRequest();
      const savedPlan = plan
        ? await monthlyPlanService.update(plan.id, request)
        : await monthlyPlanService.create({ year: period.year, month: period.month, ...request });
      onSaved(savedPlan);
    } catch (error: any) {
      setErrors([error.message || 'Помилка збереження плану']);
      showNotification('Помилка збереження плану', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = (): void => {
    if (hasChanges()) {
      setShowConfirmCancel(true);
    } else {
      onCancel();
    }
  };

  const sourcePeriod = template.sourcePlanYear && template.sourcePlanMonth
    ? { year: template.sourcePlanYear, month: template.sourcePlanMonth }
    : null;

  return (
    <div className="monthly-plan-editor">
      {!plan && (
        <div className="budget-ai-context-banner">
          <InfoIcon />
          <p>
            {sourcePeriod
              ? `Регулярні платежі підтягнуто з шаблонів витрат, а дохід, власні витрати та цілі заощаджень — з плану на ${formatPeriod(sourcePeriod).toLowerCase()}. Перевірте суми та завершіть план.`
              : 'Регулярні платежі підтягнуто з шаблонів витрат. Вкажіть дохід, цілі заощаджень і перевірте розподіл по тижнях.'}
          </p>
        </div>
      )}

      {/* 1. Дохід */}
      <div className="budget-card monthly-plan-section">
        <div className="monthly-plan-section-header">
          <div>
            <h3 className="budget-card-title">1. Дохід</h3>
            <p className="monthly-plan-section-subtitle">Зарплата та інші надходження цього місяця</p>
          </div>
          <span className="monthly-plan-section-total">{formatCurrency(summary.totalIncome)}</span>
        </div>
        <div className="monthly-plan-section-body">
          {incomes.map(row => (
            <div key={row.key} className="budget-category-item">
              <div className="budget-category-select">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Наприклад: Зарплата"
                  value={row.name}
                  maxLength={ValidationConstants.MonthlyPlan.ItemNameMaxLength}
                  onChange={e => updateIncome(row.key, { name: e.target.value })}
                />
              </div>
              <div className="budget-category-amount monthly-plan-amount">
                <input
                  type="number"
                  inputMode="decimal"
                  className="form-input budget-category-amount-input"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  max={ValidationConstants.MonthlyPlan.MaxAmount}
                  value={row.amount}
                  onChange={e => updateIncome(row.key, { amount: e.target.value })}
                />
              </div>
              <button
                type="button"
                className="budget-category-remove"
                onClick={() => setIncomes(rows => rows.filter(item => item.key !== row.key))}
                title="Видалити дохід"
              >
                <TrashIcon />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-secondary monthly-plan-add-btn"
            onClick={() => setIncomes(rows => [...rows, createIncomeRow()])}
            disabled={incomes.length >= ValidationConstants.MonthlyPlan.MaxItemsCount}
          >
            <PlusIcon />
            Додати дохід
          </button>
        </div>
      </div>

      {/* 2. Сталі витрати */}
      <div className="budget-card monthly-plan-section">
        <div className="monthly-plan-section-header">
          <div>
            <h3 className="budget-card-title">2. Сталі витрати</h3>
            <p className="monthly-plan-section-subtitle">Регулярні платежі з шаблонів та власні обов'язкові витрати</p>
          </div>
          <span className="monthly-plan-section-total">{formatCurrency(summary.totalFixed)}</span>
        </div>
        <div className="monthly-plan-section-body">
          {templateRows.length > 0 ? (
            <div className="budget-ai-options-toolbar">
              <span className="budget-ai-options-count">
                Обрано шаблонів: {selectedTemplateRows.length} / {templateRows.length}
              </span>
              <div className="budget-ai-options-links">
                <button type="button" className="budget-ai-link-btn" onClick={() => setAllTemplatesSelected(true)}>Всі</button>
                <span className="budget-ai-link-sep">·</span>
                <button type="button" className="budget-ai-link-btn" onClick={() => setAllTemplatesSelected(false)}>Жодного</button>
              </div>
            </div>
          ) : (
            <div className="btm-tmpl-no-history">
              <InfoIcon />
              <p>
                Шаблонів витрат ще немає. Створіть їх, щоб регулярні платежі підтягувались автоматично,
                або додайте власні витрати нижче.{' '}
                <button type="button" className="budget-ai-link-btn" onClick={() => navigate('/dashboard/recurring')}>
                  Перейти до шаблонів
                </button>
              </p>
            </div>
          )}

          {fixedExpenses.length > 0 && (
            <div className="budget-ai-options-list">
              {fixedExpenses.map(row => (row.isCustom ? (
                <div key={row.key} className="monthly-plan-custom-item">
                  <div className="budget-category-item">
                    <div className="budget-category-select">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Назва, наприклад: Подарунки"
                        value={row.name}
                        maxLength={ValidationConstants.MonthlyPlan.ItemNameMaxLength}
                        onChange={e => updateFixed(row.key, { name: e.target.value })}
                      />
                    </div>
                    <div className="budget-category-amount monthly-plan-amount">
                      <input
                        type="number"
                        inputMode="decimal"
                        className="form-input budget-category-amount-input"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        max={ValidationConstants.MonthlyPlan.MaxAmount}
                        value={row.amount}
                        onChange={e => updateFixed(row.key, { amount: e.target.value })}
                      />
                    </div>
                    <button
                      type="button"
                      className="budget-category-remove"
                      onClick={() => setFixedExpenses(rows => rows.filter(item => item.key !== row.key))}
                      title="Видалити витрату"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                  <GlassSelect
                    options={categoryOptions}
                    value={row.categoryId}
                    onChange={value => updateFixed(row.key, {
                      categoryId: value,
                      categoryName: categories.find(category => category.id === value)?.name ?? null
                    })}
                    placeholder="Категорія для автоматичного обліку"
                    searchable
                    allowCustomValue={true}
                    onCustomValue={categoryName => handleCreateCategory(row.key, categoryName)}
                  />
                </div>
              ) : (
                <label key={row.key} className={`btm-tmpl-row ${row.isSelected ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    className="budget-ai-checkbox"
                    checked={row.isSelected}
                    onChange={() => updateFixed(row.key, { isSelected: !row.isSelected })}
                  />
                  <div className="btm-tmpl-row-body">
                    <span className="btm-tmpl-row-name">{row.name}</span>
                    <span className="btm-tmpl-row-meta">
                      {[getRecurrenceLabel(row.recurrenceType, row.dayOfMonth, row.paymentsCount), row.categoryName, row.accountName]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                  <div className="btm-tmpl-row-amount-wrap">
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      className={`btm-tmpl-amount-input ${!row.isSelected ? 'disabled' : ''}`}
                      value={row.amount}
                      disabled={!row.isSelected}
                      onChange={e => updateFixed(row.key, { amount: e.target.value })}
                      onClick={e => e.stopPropagation()}
                      placeholder="0"
                    />
                    <span className="btm-tmpl-currency">₴</span>
                  </div>
                </label>
              )))}
            </div>
          )}

          <button
            type="button"
            className="btn btn-secondary monthly-plan-add-btn"
            onClick={() => setFixedExpenses(rows => [...rows, createCustomRow()])}
            disabled={fixedExpenses.length >= ValidationConstants.MonthlyPlan.MaxItemsCount}
          >
            <PlusIcon />
            Додати свою витрату
          </button>
        </div>
      </div>

      {/* 3. Заощадження */}
      <div className="budget-card monthly-plan-section">
        <div className="monthly-plan-section-header">
          <div>
            <h3 className="budget-card-title">3. Заощадження</h3>
            <p className="monthly-plan-section-subtitle">Скільки відкласти цього місяця на рахунки заощаджень</p>
          </div>
          <span className="monthly-plan-section-total">{formatCurrency(summary.totalSavings)}</span>
        </div>
        <div className="monthly-plan-section-body">
          {savingsGoals.length > 0 ? (
            <div className="budget-ai-options-list">
              {savingsGoals.map(row => (
                <label key={row.key} className={`btm-tmpl-row ${row.isSelected ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    className="budget-ai-checkbox"
                    checked={row.isSelected}
                    onChange={() => updateSavings(row.key, { isSelected: !row.isSelected })}
                  />
                  <div className="btm-tmpl-row-body">
                    <span className="btm-tmpl-row-name">{row.accountName}</span>
                    <span className="btm-tmpl-row-meta">
                      {row.currentBalance !== null
                        ? `Баланс: ${formatCurrency(row.currentBalance)} ${getCurrencyName(parseCurrency(row.currency))}`
                        : getCurrencyName(parseCurrency(row.currency))}
                    </span>
                  </div>
                  <div className="btm-tmpl-row-amount-wrap">
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      className={`btm-tmpl-amount-input ${!row.isSelected ? 'disabled' : ''}`}
                      value={row.amount}
                      disabled={!row.isSelected}
                      onChange={e => updateSavings(row.key, { amount: e.target.value })}
                      onClick={e => e.stopPropagation()}
                      placeholder="0"
                    />
                    <span className="btm-tmpl-currency">₴</span>
                  </div>
                </label>
              ))}
            </div>
          ) : (
            <div className="btm-tmpl-no-history">
              <InfoIcon />
              <p>
                Рахунків для заощаджень ще немає. Позначте рахунок галочкою «Рахунок для заощаджень»,
                і він з'явиться тут.{' '}
                <button type="button" className="budget-ai-link-btn" onClick={() => navigate('/dashboard/accounts')}>
                  Перейти до рахунків
                </button>
              </p>
            </div>
          )}
          <p className="monthly-plan-hint">
            Прогрес рахується за переказами між звичайними рахунками та рахунками заощаджень у сумі, що списана з бюджету.
          </p>
        </div>
      </div>

      {/* 4. Розподіл */}
      <div className="budget-card monthly-plan-section">
        <div className="monthly-plan-section-header">
          <div>
            <h3 className="budget-card-title">4. Розподіл по тижнях</h3>
            <p className="monthly-plan-section-subtitle">Оцінка вільних коштів оновлюється одразу під час редагування</p>
          </div>
        </div>
        <div className="monthly-plan-section-body">
          <div className="budget-stats-grid">
            <div className="budget-stat">
              <div className="budget-stat-label">Дохід</div>
              <div className="budget-stat-value budget-stat-total">{formatCurrency(summary.totalIncome)}</div>
            </div>
            <div className="budget-stat">
              <div className="budget-stat-label">Сталі та заощадження</div>
              <div className="budget-stat-value">{formatCurrency(summary.totalFixed + summary.totalSavings)}</div>
            </div>
            <div className={`budget-stat budget-stat-remaining ${summary.freeAmount < 0 ? 'budget-stat-over budget-stat-progress-over' : 'budget-stat-progress-good'}`}>
              <div className="budget-stat-label">Вільні кошти</div>
              <div className="budget-stat-value">{formatCurrency(summary.freeAmount)}</div>
            </div>
          </div>

          {summary.freeAmount < 0 && (
            <div className="budget-ai-context-banner btm-tmpl-confidence-low">
              <InfoIcon />
              <p>
                Сталі витрати та заощадження перевищують дохід на <strong>{formatCurrency(Math.abs(summary.freeAmount))}</strong>.
                Зменшіть суми або додайте дохід.
              </p>
            </div>
          )}

          {hasMonthActivity && (
            <div className="budget-ai-context-banner">
              <InfoIcon />
              <p>Прогноз уже враховує операції, внесені за цей місяць.</p>
            </div>
          )}

          <WeeklyBreakdown weeks={summary.weeks} period={period} />

          <p className="monthly-plan-hint">
            Невитрачені кошти тижня переходять на наступні тижні, а перевитрата — зменшує їх.
          </p>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="error-message general-error monthly-plan-errors">
          {errors.map(error => (
            <div key={error}>{error}</div>
          ))}
        </div>
      )}

      <div className="budget-card monthly-plan-editor-actions">
        <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={isSaving}>
          Скасувати
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Збереження...' : plan ? 'Зберегти зміни' : 'Завершити план'}
        </button>
      </div>

      <ConfirmationModal
        isOpen={showConfirmCancel}
        title="Скасувати зміни?"
        message="У вас є незбережені зміни плану. Ви впевнені, що хочете вийти без збереження?"
        confirmText="Так"
        cancelText="Скасувати"
        onConfirm={() => {
          setShowConfirmCancel(false);
          onCancel();
        }}
        onCancel={() => setShowConfirmCancel(false)}
        variant="warning"
      />
    </div>
  );
};

export default MonthlyPlanEditor;
